import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({ meta: [{ title: "Reset password · CARIO INTEL" }] }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
      toast.success("Reset link sent. Check your inbox.");
    } catch (e: any) {
      toast.error(e?.message || "Could not send reset email");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Back to sign in →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Account recovery</p>
          <h1 className="mt-2 font-display text-3xl">Reset your password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter the email associated with your operator account. We'll send a secure link to set a new password.
          </p>
          {sent ? (
            <div className="mt-6 rounded-md border border-success/40 bg-success/10 p-4 text-sm text-success">
              If an account exists for <span className="font-mono">{email}</span>, a reset link is on its way. Check your inbox (and spam).
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}